import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useAuth } from "context/AuthContext";
import { useCart } from "context/CartContext";
import { useCurrency } from "context/CurrencyContext";
import { get, post } from "lib/api";
import { getStorefrontConfig } from "lib/storefront";
import {
  stripePublishableKeyForCompany,
} from "lib/companyProfile";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { usePlaidLink } from "react-plaid-link";
import PropTypes from "prop-types";
import { useResolvedStorefrontLayout } from "storefronts/StorefrontLayoutBridge";
import { useStorefrontTenant } from "context/StorefrontTenantContext";
import { storefrontThemeForTenant } from "lib/storefrontTheme";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import MobileCheckoutFlow from "mobile/components/MobileCheckoutFlow";

const CHECKOUT_STEPS = ["cart", "shipping", "delivery", "payment", "review"];
const REQUEST_STEPS = ["cart", "shipping", "review"];

function normalizeAddress(row, index = 0) {
  if (!row || typeof row !== "object") return null;
  const addressId = String(
    row.addressId || row.id || row.componentId || `address-${index}`,
  ).trim();
  return {
    ...row,
    addressId,
    label: String(row.label || row.name || `Address ${index + 1}`).trim(),
    name: String(row.name || row.contactName || "").trim(),
    street: String(row.street || row.street1 || "").trim(),
    city: String(row.city || "").trim(),
    state: String(row.state || "").trim(),
    postalCode: String(row.postalCode || row.zip || "").trim(),
    country: String(row.country || "")
      .trim()
      .toUpperCase(),
    phone: String(row.phone || "").trim(),
    isPrimary: !!row.isPrimary,
  };
}

function toShippingAddress(row) {
  if (!row) return null;
  return {
    name: row.name || row.label || "",
    street1: row.street || "",
    street2: row.street2 || "",
    city: row.city || "",
    state: row.state || "",
    zip: row.postalCode || row.zip || "",
    country: row.country || "",
    phone: row.phone || "",
  };
}

function addressSummary(row) {
  if (!row) return "Choose a shipping address to continue.";
  return [
    row.street || row.street1,
    `${row.city || ""}, ${row.state || ""} ${
      row.postalCode || row.zip || ""
    }`.trim(),
    row.country,
  ]
    .filter((part) => String(part || "").trim())
    .join(" • ");
}

function getStripePublishableKey(companyKey) {
  return stripePublishableKeyForCompany(companyKey);
}

function formatRateLabel(r, formatMoney) {
  const carrier = r?.carrier ? String(r.carrier) : "";
  const service = r?.service ? String(r.service) : "";
  const rate = r?.rate != null ? Number(r.rate) : null;
  const totalWithProcessingFee =
    r?.totalWithProcessingFee != null ? Number(r.totalWithProcessingFee) : null;
  const processingFeeAmount =
    r?.processingFeeAmount != null ? Number(r.processingFeeAmount) : 0;
  const currency = r?.currency ? String(r.currency).toUpperCase() : "";
  const days = r?.delivery_days != null ? String(r.delivery_days) : "";
  const amountValue =
    totalWithProcessingFee != null
      ? totalWithProcessingFee
      : rate != null
        ? rate
        : null;
  const amountText =
    amountValue != null && formatMoney
      ? formatMoney(amountValue)
      : amountValue != null
        ? amountValue.toFixed(2)
        : "?";
  const feeText =
    processingFeeAmount > 0
      ? ` • incl. fee ${formatMoney ? formatMoney(processingFeeAmount) : processingFeeAmount.toFixed(2)}${currency && !formatMoney ? ` ${currency}` : ""}`
      : "";
  return `${carrier} ${service} — ${amountText}${currency && !formatMoney ? ` ${currency}` : ""}${
    days ? ` (${days} days)` : ""
  }${feeText}`;
}

function StripePayment({ clientSecret, orderNumber, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/account`,
        },
        redirect: "if_required",
      });
      if (error) {
        setMessage({ type: "error", text: error.message || "Payment failed" });
      } else if (
        paymentIntent?.status === "succeeded" ||
        paymentIntent?.status === "processing"
      ) {
        setMessage({
          type: "success",
          text: `Payment ${paymentIntent.status}. Order: ${orderNumber}`,
        });
        if (onPaid) onPaid(paymentIntent);
      } else {
        setMessage({
          type: "info",
          text: `Payment status: ${paymentIntent?.status || "unknown"}`,
        });
      }
    } catch (err) {
      const msg = err?.message || "Payment failed";
      setMessage({ type: "error", text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit}>
      {message ? (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      ) : null}
      <PaymentElement />
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mt: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <MDButton
          type="submit"
          variant="gradient"
          color="info"
          disabled={!stripe || !elements || submitting}
        >
          {submitting ? "Processing..." : "Pay now"}
        </MDButton>
        <MDTypography variant="button" color="text" sx={{ opacity: 0.8 }}>
          Order: {orderNumber}
        </MDTypography>
        <MDTypography variant="button" color="text" sx={{ opacity: 0.6 }}>
          (clientSecret: {String(clientSecret || "").slice(0, 18)}…)
        </MDTypography>
      </Box>
    </Box>
  );
}

StripePayment.propTypes = {
  clientSecret: PropTypes.string.isRequired,
  orderNumber: PropTypes.string.isRequired,
  onPaid: PropTypes.func,
};

StripePayment.defaultProps = {
  onPaid: null,
};

export default function Checkout() {
  const StorefrontLayout = useResolvedStorefrontLayout();
  const { token, pluginRole, storefrontTenant } = useAuth();
  const { tenant, tenantKey } = useStorefrontTenant();
  const { t } = useStorefrontCopy();
  const { currency, formatUSDForDisplay } = useCurrency();
  const storefrontTheme = storefrontThemeForTenant(tenant);
  const { lines, subtotal, clear } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const storefront = getStorefrontConfig();

  const role = String(pluginRole || "").toLowerCase();
  const isCustomer = role === "customer";
  const isDistributor = role === "distributor";
  const canUseCheckout = isCustomer || isDistributor;
  const companyKey = String(
    tenantKey || storefrontTenant || storefront?.companyKey || "",
  ).toLowerCase();
  const isMx = currency === "MXN" || companyKey === "mx";
  const stepTitle = useMemo(
    () => ({
      cart: t("cart.title", "Cart"),
      shipping: t("checkout.shipping", "Shipping"),
      delivery: isMx ? "Entrega" : "Delivery",
      payment: t("checkout.payment", "Payment"),
      review: isMx ? "Revisión" : "Review",
    }),
    [isMx, t],
  );

  const [meLoading, setMeLoading] = useState(true);
  const [meError, setMeError] = useState(null);
  const [satMissing, setSatMissing] = useState([]);
  const [wantsFacturaToRfc, setWantsFacturaToRfc] = useState(false);
  const [facturaLoaded, setFacturaLoaded] = useState(false);
  const [checkoutProfile, setCheckoutProfile] = useState({
    customerKind: "",
    paymentTerms: "",
    creditLimit: 0,
  });
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [selectedShippingAddressId, setSelectedShippingAddressId] =
    useState("");
  const [shippingAddressMode, setShippingAddressMode] = useState("saved");
  const [currentStep, setCurrentStep] = useState("cart");
  const [locationMessage, setLocationMessage] = useState(null);
  const [selectedPaymentCollectionMethod, setSelectedPaymentCollectionMethod] =
    useState("stripe_card");
  const [postCheckoutMessage, setPostCheckoutMessage] = useState(null);
  const [bankTransferInstructions, setBankTransferInstructions] =
    useState(null);
  const [checkoutPaymentOptions, setCheckoutPaymentOptions] = useState({
    bankTransferAvailable: false,
    plaidTransferAvailable: false,
  });

  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState(null);
  const [shipmentId, setShipmentId] = useState(null);
  const [rates, setRates] = useState([]);
  const [selectedRateId, setSelectedRateId] = useState(null);
  const [processingFeeConfig, setProcessingFeeConfig] = useState(null);
  const [pricing, setPricing] = useState(null);

  const selectedRate = useMemo(
    () =>
      (rates || []).find((r) => String(r.id) === String(selectedRateId)) ||
      null,
    [rates, selectedRateId],
  );
  const selectedSavedAddress = useMemo(
    () =>
      (shippingAddresses || []).find(
        (row) => String(row.addressId) === String(selectedShippingAddressId),
      ) ||
      (shippingAddresses || []).find((row) => row.isPrimary) ||
      (shippingAddresses || [])[0] ||
      null,
    [shippingAddresses, selectedShippingAddressId],
  );
  const shippingAmount =
    selectedRate?.rate != null ? Number(selectedRate.rate) : 0;
  const processingFeeAmount =
    selectedRate?.processingFeeAmount != null
      ? Number(selectedRate.processingFeeAmount)
      : 0;
  const orderTotal =
    pricing?.total != null
      ? Number(pricing.total)
      : selectedRate?.totalWithProcessingFee != null
        ? Number(selectedRate.totalWithProcessingFee)
        : Number(subtotal || 0) +
          (Number.isFinite(shippingAmount) ? shippingAmount : 0) +
          processingFeeAmount;

  const [intentLoading, setIntentLoading] = useState(false);
  const [intentError, setIntentError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [orderNumber, setOrderNumber] = useState(null);
  const [plaidTransferLinkToken, setPlaidTransferLinkToken] = useState(null);
  const [plaidSalesOrderDocumentId, setPlaidSalesOrderDocumentId] =
    useState(null);
  const openedPlaidTokenRef = useRef(null);

  const stripePublishableKey = getStripePublishableKey(companyKey);
  const stripePromise = useMemo(
    () => (stripePublishableKey ? loadStripe(stripePublishableKey) : null),
    [stripePublishableKey],
  );

  const plaidLink = usePlaidLink({
    token: plaidTransferLinkToken,
    onSuccess: async (publicToken, metadata) => {
      setIntentLoading(true);
      setIntentError(null);
      try {
        const accountId =
          metadata?.account_id || metadata?.accounts?.[0]?.id || null;
        const res = await post("/api/storefront/plaid-transfer/pay", {
          data: {
            salesOrderDocumentId: plaidSalesOrderDocumentId,
            publicToken,
            accountId,
            metadata,
          },
        });
        clear();
        resetCheckoutSession();
        setPlaidTransferLinkToken(null);
        setPlaidSalesOrderDocumentId(null);
        openedPlaidTokenRef.current = null;
        setPostCheckoutMessage({
          type: "success",
          text: `Pay-by-bank payment is processing for order ${
            orderNumber || res?.data?.salesOrder?.salesOrderNumber || "created"
          }. Funds will post after settlement.`,
        });
      } catch (e) {
        const msg =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to create Plaid Transfer payment";
        setIntentError(msg);
      } finally {
        setIntentLoading(false);
      }
    },
    onExit: () => {
      setPlaidTransferLinkToken(null);
      openedPlaidTokenRef.current = null;
    },
  });

  useEffect(() => {
    if (
      !plaidTransferLinkToken ||
      !plaidLink.ready ||
      openedPlaidTokenRef.current === plaidTransferLinkToken
    )
      return;
    openedPlaidTokenRef.current = plaidTransferLinkToken;
    plaidLink.open();
  }, [plaidTransferLinkToken, plaidLink]);

  const [toAddress, setToAddress] = useState({
    name: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    zip: "",
    country: isMx ? "MX" : "US",
    phone: "",
  });

  const normalizedCustomerKind = String(checkoutProfile.customerKind || "")
    .trim()
    .toLowerCase();
  const usesDistributorRequestFlow =
    isCustomer &&
    normalizedCustomerKind === "end_user" &&
    !!checkoutProfile.linkedDistributorDocumentId;
  const checkoutSteps = usesDistributorRequestFlow
    ? REQUEST_STEPS
    : CHECKOUT_STEPS;
  const currentStepIndex = Math.max(0, checkoutSteps.indexOf(currentStep));
  const activeShipTo =
    shippingAddressMode === "manual"
      ? toAddress
      : toShippingAddress(selectedSavedAddress);
  const activeShipToSummary =
    shippingAddressMode === "manual"
      ? addressSummary(toAddress)
      : addressSummary(selectedSavedAddress);

  const resetCheckoutSession = () => {
    setShippingLoading(false);
    setShippingError(null);
    setShipmentId(null);
    setRates([]);
    setSelectedRateId(null);
    setProcessingFeeConfig(null);
    setPricing(null);
    setIntentLoading(false);
    setIntentError(null);
    setClientSecret(null);
    setOrderNumber(null);
    setBankTransferInstructions(null);
    setCheckoutPaymentOptions({
      bankTransferAvailable: false,
      plaidTransferAvailable: false,
    });
    setPlaidTransferLinkToken(null);
    setPlaidSalesOrderDocumentId(null);
    openedPlaidTokenRef.current = null;
    setToAddress({
      name: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      zip: "",
      country: isMx ? "MX" : "US",
      phone: "",
    });
  };

  useEffect(() => {
    if (!token) return;
    (async () => {
      setMeLoading(true);
      setMeError(null);
      try {
        const res = await get("/api/storefront/me");
        const d = res?.data?.attributes
          ? {
              id: res.data.id,
              documentId: res.data.documentId || res.data.id,
              ...res.data.attributes,
            }
          : res?.data || null;
        setCheckoutProfile({
          customerKind: String(d?.customerKind || "")
            .trim()
            .toLowerCase(),
          paymentTerms: String(d?.paymentTerms || "").trim(),
          creditLimit: Number(d?.creditLimit || 0) || 0,
          linkedDistributorDocumentId: String(
            d?.linkedDistributorDocumentId || "",
          ).trim(),
          linkedDistributorName: String(d?.linkedDistributorName || "").trim(),
        });
        const normalizedAddresses = (
          Array.isArray(d?.addresses) ? d.addresses : []
        )
          .map((row, index) => normalizeAddress(row, index))
          .filter(Boolean);
        setShippingAddresses(normalizedAddresses);
        setSelectedShippingAddressId((current) => {
          if (
            current &&
            normalizedAddresses.some(
              (row) => String(row.addressId) === String(current),
            )
          )
            return current;
          return String(
            d?.primaryAddressId ||
              normalizedAddresses.find((row) => row.isPrimary)?.addressId ||
              normalizedAddresses[0]?.addressId ||
              "",
          );
        });
        if (!normalizedAddresses.length) setShippingAddressMode("manual");

        if (isMx) {
          if (!facturaLoaded) {
            setWantsFacturaToRfc(!!d?.wantsFacturaMxByDefault);
            setFacturaLoaded(true);
          }

          const missing = [];
          const rfc = String(d?.rfc || d?.taxId || "").trim();
          const regimenFiscal = String(d?.regimenFiscal || "").trim();
          const usoCfdi = String(d?.usoCfdi || "").trim();
          const fiscalZip = String(
            d?.fiscalZip || d?.fiscalAddress?.postalCode || "",
          ).trim();
          if (!rfc) missing.push("RFC");
          if (!regimenFiscal) missing.push("Regimen Fiscal");
          if (!usoCfdi) missing.push("Uso CFDI");
          if (!fiscalZip) missing.push("Fiscal ZIP");
          setSatMissing(missing);
        } else {
          setSatMissing([]);
        }
      } catch (e) {
        const msg =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to load profile";
        setMeError(msg);
      } finally {
        setMeLoading(false);
      }
    })();
  }, [token, isMx, facturaLoaded]);

  useEffect(() => {
    if (!Array.isArray(lines) || lines.length === 0) {
      navigate(location.pathname.startsWith("/m/") ? "/m/cart" : "/cart");
    }
  }, [lines, navigate, location.pathname]);

  const canCheckoutMx =
    !isMx ||
    !wantsFacturaToRfc ||
    (Array.isArray(satMissing) && satMissing.length === 0);
  const hasActiveShipTo =
    shippingAddressMode === "saved"
      ? !!selectedSavedAddress
      : String(toAddress.street1 || "").trim() &&
        String(toAddress.city || "").trim() &&
        String(toAddress.zip || "").trim() &&
        String(toAddress.country || "").trim();
  const canQuote = canCheckoutMx && !meLoading && hasActiveShipTo;
  const distributorHasCredit =
    isDistributor &&
    (String(checkoutProfile.paymentTerms || "")
      .trim()
      .toUpperCase()
      .startsWith("NET") ||
      Number(checkoutProfile.creditLimit || 0) > 0);
  const paymentCollectionMethod = distributorHasCredit
    ? "on_account"
    : selectedPaymentCollectionMethod;

  if (!token)
    return (
      <Navigate
        to={
          location.pathname.startsWith("/m/")
            ? "/m/authentication/sign-in"
            : "/authentication/sign-in"
        }
        state={{ from: location }}
        replace
      />
    );
  if (!canUseCheckout)
    return (
      <Navigate
        to={location.pathname.startsWith("/m/") ? "/m" : "/dashboard"}
        replace
      />
    );

  const onQuote = async () => {
    setShippingLoading(true);
    setShippingError(null);
    setShipmentId(null);
    setRates([]);
    setSelectedRateId(null);
    setProcessingFeeConfig(null);
    setPricing(null);
    setIntentError(null);
    setClientSecret(null);
    setOrderNumber(null);
    setPostCheckoutMessage(null);
    setBankTransferInstructions(null);
    try {
      const res = await post("/api/storefront/shipping/quote", {
        data: {
          ...(shippingAddressMode === "saved"
            ? { shippingAddressId: selectedSavedAddress?.addressId }
            : { toAddress: activeShipTo }),
          lines: (lines || []).map((l) => ({
            documentId: l.documentId,
            qty: l.qty,
          })),
        },
      });
      setShipmentId(res?.shipmentId || null);
      setRates(Array.isArray(res?.rates) ? res.rates : []);
      setProcessingFeeConfig(res?.processingFee || null);
      setCheckoutPaymentOptions({
        bankTransferAvailable: !!res?.paymentOptions?.bankTransferAvailable,
        plaidTransferAvailable: !!res?.paymentOptions?.plaidTransferAvailable,
      });
    } catch (e) {
      const msg =
        e?.response?.data?.error?.message ||
        e?.message ||
        "Failed to quote shipping";
      setShippingError(msg);
    } finally {
      setShippingLoading(false);
    }
  };

  const onCreateIntent = async () => {
    setIntentLoading(true);
    setIntentError(null);
    try {
      const res = await post("/api/storefront/checkout/intent", {
        data: {
          lines: (lines || []).map((l) => ({
            documentId: l.documentId,
            qty: l.qty,
          })),
          shippingAmount,
          shippingService: selectedRate
            ? `${selectedRate.carrier} ${selectedRate.service}`
            : null,
          shippingRateId: selectedRate?.id || null,
          shipmentId,
          ...(shippingAddressMode === "saved"
            ? { shippingAddressId: selectedSavedAddress?.addressId }
            : { toAddress: activeShipTo }),
          paymentCollectionMethod,
          cfdiReceiverType: isMx
            ? wantsFacturaToRfc
              ? "customer"
              : "public"
            : undefined,
        },
      });
      setClientSecret(res?.paymentIntent?.clientSecret || null);
      setOrderNumber(res?.salesOrder?.salesOrderNumber || null);
      setPricing(res?.pricing || null);
      setBankTransferInstructions(res?.bankTransferInstructions || null);
      if (res?.paymentCollectionMethod === "plaid_transfer") {
        const salesOrderDocumentId =
          res?.salesOrder?.documentId || res?.salesOrder?.id || null;
        if (!salesOrderDocumentId) {
          throw new Error("Order was created without a document id.");
        }
        const linkRes = await post("/api/storefront/plaid-transfer/link-token", {});
        const linkToken = linkRes?.data?.linkToken;
        if (!linkToken) throw new Error("Plaid did not return a Link token.");
        setPlaidSalesOrderDocumentId(String(salesOrderDocumentId));
        setPlaidTransferLinkToken(linkToken);
        setPostCheckoutMessage({
          type: "info",
          text: `Order ${
            res?.salesOrder?.salesOrderNumber || "created"
          } is ready for pay-by-bank authorization.`,
        });
        return;
      }
      if (!res?.paymentIntent?.clientSecret) {
        clear();
        resetCheckoutSession();
        setPostCheckoutMessage({
          type:
            res?.paymentCollectionMethod === "bank_transfer"
              ? "info"
              : "success",
          text:
            res?.paymentCollectionMethod === "bank_transfer"
              ? `Order ${
                  res?.salesOrder?.salesOrderNumber || "created"
                } is pending bank transfer payment.`
              : `Order ${
                  res?.salesOrder?.salesOrderNumber || "created"
                } was placed on account.`,
        });
        return;
      }
    } catch (e) {
      const msg =
        e?.response?.data?.error?.message ||
        e?.message ||
        "Failed to create checkout payment";
      setIntentError(msg);
    } finally {
      setIntentLoading(false);
    }
  };

  const onCreateDistributorRequest = async () => {
    setIntentLoading(true);
    setIntentError(null);
    try {
      const res = await post("/api/storefront/distributor-orders/requests", {
        data: {
          lines: (lines || []).map((l) => ({
            documentId: l.documentId,
            qty: l.qty,
          })),
          ...(shippingAddressMode === "saved"
            ? { shippingAddressId: selectedSavedAddress?.addressId }
            : { toAddress: activeShipTo }),
        },
      });
      clear();
      setPostCheckoutMessage({
        type: "success",
        text: `Request ${
          res?.data?.documentId || res?.documentId || "created"
        } sent to ${
          checkoutProfile.linkedDistributorName || "your distributor"
        }.`,
      });
      navigate(
        location.pathname.startsWith("/m/")
          ? "/m/account/orders"
          : "/account/orders",
      );
    } catch (e) {
      const msg =
        e?.response?.data?.error?.message ||
        e?.message ||
        "Failed to submit distributor request";
      setIntentError(msg);
    } finally {
      setIntentLoading(false);
    }
  };

  const useCurrentLocation = () => {
    setLocationMessage(null);
    if (!navigator.geolocation) {
      setLocationMessage({
        type: "warning",
        text: "Current location is not available in this browser.",
      });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => {
        setShippingAddressMode("manual");
        setLocationMessage({
          type: "info",
          text: "Location permission worked. Mobile web still needs a street, city, and ZIP for the shipping carrier quote.",
        });
      },
      (error) => {
        setLocationMessage({
          type: "warning",
          text: error?.message || "Unable to access current location.",
        });
      },
      { maximumAge: 300000, timeout: 5000, enableHighAccuracy: false },
    );
  };

  const goNext = () => {
    const next = checkoutSteps[currentStepIndex + 1];
    if (next) setCurrentStep(next);
  };
  const goBack = () => {
    const previous = checkoutSteps[currentStepIndex - 1];
    if (previous) setCurrentStep(previous);
  };

  const primaryAction = () => {
    if (currentStep === "delivery") {
      if (!selectedRateId) return;
      goNext();
      return;
    }
    if (currentStep === "review") {
      if (usesDistributorRequestFlow) onCreateDistributorRequest();
      else onCreateIntent();
      return;
    }
    goNext();
  };

  const primaryDisabled =
    (currentStep === "shipping" && !hasActiveShipTo) ||
    (currentStep === "delivery" && (!selectedRateId || shippingLoading)) ||
    (currentStep === "review" &&
      (!canCheckoutMx ||
        intentLoading ||
        (!usesDistributorRequestFlow && !selectedRateId)));

  const isMobileCheckout = location.pathname.startsWith("/m/");
  const primaryLabel =
    currentStep === "review"
      ? intentLoading
        ? "Creating..."
        : usesDistributorRequestFlow
          ? "Submit to distributor"
          : paymentCollectionMethod === "on_account"
            ? "Place on account"
            : paymentCollectionMethod === "bank_transfer"
              ? "Create transfer order"
              : paymentCollectionMethod === "plaid_transfer"
                ? plaidTransferLinkToken
                  ? "Plaid authorization ready"
                  : "Pay by bank account"
                : clientSecret
                  ? "Payment ready"
                  : "Continue to payment"
      : `Continue to ${
          stepTitle[checkoutSteps[currentStepIndex + 1]] || "next"
        }`;

  if (isMobileCheckout) {
    return (
      <MobileCheckoutFlow
        isMx={isMx}
        currency={currency}
        formatMoney={formatUSDForDisplay}
        stepTitle={stepTitle}
        checkoutSteps={checkoutSteps}
        currentStep={currentStep}
        currentStepIndex={currentStepIndex}
        setCurrentStep={setCurrentStep}
        usesDistributorRequestFlow={usesDistributorRequestFlow}
        linkedDistributorName={checkoutProfile.linkedDistributorName}
        meLoading={meLoading}
        meError={meError}
        intentError={intentError}
        shippingError={shippingError}
        postCheckoutMessage={postCheckoutMessage}
        bankTransferInstructions={bankTransferInstructions}
        lines={lines || []}
        subtotal={subtotal}
        shippingAddressMode={shippingAddressMode}
        setShippingAddressMode={setShippingAddressMode}
        shippingAddresses={shippingAddresses}
        selectedSavedAddress={selectedSavedAddress}
        setSelectedShippingAddressId={setSelectedShippingAddressId}
        toAddress={toAddress}
        setToAddress={setToAddress}
        useCurrentLocation={useCurrentLocation}
        locationMessage={locationMessage}
        wantsFacturaToRfc={wantsFacturaToRfc}
        setWantsFacturaToRfc={setWantsFacturaToRfc}
        satMissing={satMissing}
        canCheckoutMx={canCheckoutMx}
        onNavigateProfile={() => navigate("/m/account/profile")}
        activeShipToSummary={activeShipToSummary}
        onQuote={onQuote}
        canQuote={canQuote}
        shippingLoading={shippingLoading}
        rates={rates}
        selectedRateId={selectedRateId}
        setSelectedRateId={setSelectedRateId}
        formatRateLabel={(rate) => formatRateLabel(rate, formatUSDForDisplay)}
        isDistributor={isDistributor}
        distributorHasCredit={distributorHasCredit}
        bankTransferAvailable={checkoutPaymentOptions.bankTransferAvailable}
        plaidTransferAvailable={checkoutPaymentOptions.plaidTransferAvailable}
        checkoutProfile={checkoutProfile}
        paymentCollectionMethod={paymentCollectionMethod}
        setSelectedPaymentCollectionMethod={setSelectedPaymentCollectionMethod}
        stripePublishableKey={stripePublishableKey}
        selectedRate={selectedRate}
        pricing={pricing}
        shippingAmount={shippingAmount}
        processingFeeAmount={processingFeeAmount}
        processingFeeConfig={processingFeeConfig}
        orderTotal={orderTotal}
        clientSecret={clientSecret}
        stripePromise={stripePromise}
        orderNumber={orderNumber}
        StripePayment={StripePayment}
        onStripePaid={() => {
          clear();
          resetCheckoutSession();
          setPostCheckoutMessage({
            type: "success",
            text: `Payment received for order ${orderNumber || "created"}.`,
          });
        }}
        plaidSalesOrderDocumentId={plaidSalesOrderDocumentId}
        onBack={
          currentStepIndex === 0
            ? () => navigate("/m/cart")
            : goBack
        }
        primaryAction={primaryAction}
        primaryDisabled={primaryDisabled}
        primaryLabel={primaryLabel}
      />
    );
  }

  const checkoutContent = (
    <Card
      variant="outlined"
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius:
          storefrontTheme.preset === "industrial" ||
          storefrontTheme.preset === "commerce"
            ? 0
            : 3,
        borderColor:
          storefrontTheme.preset === "industrial"
            ? "transparent"
            : undefined,
        borderWidth:
          storefrontTheme.preset === "industrial" ||
          storefrontTheme.preset === "commerce"
            ? 0
            : undefined,
        boxShadow:
          storefrontTheme.preset === "commerce"
            ? "0 26px 72px rgba(16,24,40,0.10)"
            : "none",
      }}
    >
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
      >
        <MDBox>
          <MDTypography variant="h4" fontWeight={900} gutterBottom>
            {stepTitle[currentStep]}
          </MDTypography>
          <MDTypography variant="body2" color="text" sx={{ opacity: 0.85 }}>
            {usesDistributorRequestFlow
              ? `Request routes to ${
                  checkoutProfile.linkedDistributorName || "your distributor"
                }.`
              : `${t("checkout.title", "Checkout")} (${currency}) with shipping rates and payment.`}
          </MDTypography>
        </MDBox>
        <MDTypography
          variant="caption"
          color="text"
          sx={{ opacity: 0.7, fontWeight: 900 }}
        >
          {currentStepIndex + 1} of {checkoutSteps.length}
        </MDTypography>
      </MDBox>

      <Stack
        direction="row"
        spacing={0.75}
        sx={{ mt: 2, overflowX: "auto", pb: 0.25 }}
      >
        {checkoutSteps.map((step, index) => (
          <MDButton
            key={step}
            size="small"
            variant={step === currentStep ? "contained" : "outlined"}
            color="info"
            disabled={index > currentStepIndex + 1}
            onClick={() => setCurrentStep(step)}
            sx={{ flexShrink: 0 }}
          >
            {stepTitle[step]}
          </MDButton>
        ))}
      </Stack>

      {meError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {meError}
        </Alert>
      ) : null}
      {meLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
          <CircularProgress size={18} />
          <MDTypography variant="button" color="text">
            Loading profile...
          </MDTypography>
        </Box>
      ) : null}
      {intentError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {intentError}
        </Alert>
      ) : null}
      {shippingError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {shippingError}
        </Alert>
      ) : null}
      {postCheckoutMessage ? (
        <Alert severity={postCheckoutMessage.type} sx={{ mt: 2 }}>
          {postCheckoutMessage.text}
        </Alert>
      ) : null}
      {bankTransferInstructions ? (
        <Alert severity="info" sx={{ mt: 2, whiteSpace: "pre-wrap" }}>
          {bankTransferInstructions}
        </Alert>
      ) : null}

      <Divider sx={{ my: 2 }} />

      {currentStep === "cart" ? (
        <Stack spacing={1.5}>
          {(lines || []).map((line) => (
            <Card
              key={line.documentId}
              variant="outlined"
              sx={{ p: 1.5, boxShadow: "none" }}
            >
              <MDBox display="flex" justifyContent="space-between" gap={2}>
                <MDBox sx={{ minWidth: 0 }}>
                  <MDTypography variant="h6" fontWeight={900}>
                    {line.name || line.sku || "Item"}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{ opacity: 0.75 }}
                  >
                    SKU {line.sku || "Pending"}
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="text"
                    sx={{ opacity: 0.85, mt: 0.5 }}
                  >
                    Qty {line.qty} • Unit {formatUSDForDisplay(line.unitPrice || 0)}
                    {line.coreCharge
                      ? ` • Core ${formatUSDForDisplay(line.coreCharge || 0)}`
                      : ""}
                  </MDTypography>
                </MDBox>
                <MDTypography variant="button" fontWeight={900}>
                  {formatUSDForDisplay(
                    Number(line.qty || 0) *
                      (Number(line.unitPrice || 0) + Number(line.coreCharge || 0)),
                  )}
                </MDTypography>
              </MDBox>
            </Card>
          ))}
          <Alert severity="info">
            Subtotal including refundable core deposits:{" "}
            {formatUSDForDisplay(subtotal || 0)}
          </Alert>
        </Stack>
      ) : null}

      {currentStep === "shipping" ? (
        <Stack spacing={2}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <MDButton
              variant={
                shippingAddressMode === "saved" ? "contained" : "outlined"
              }
              color="info"
              disabled={!shippingAddresses.length}
              onClick={() => setShippingAddressMode("saved")}
            >
              Saved address
            </MDButton>
            <MDButton
              variant={
                shippingAddressMode === "manual" ? "contained" : "outlined"
              }
              color="info"
              onClick={() => setShippingAddressMode("manual")}
            >
              Enter address
            </MDButton>
            <MDButton
              variant="outlined"
              color="info"
              onClick={useCurrentLocation}
            >
              Use current location
            </MDButton>
          </Box>
          {locationMessage ? (
            <Alert severity={locationMessage.type}>
              {locationMessage.text}
            </Alert>
          ) : null}

          {shippingAddressMode === "saved" ? (
            <Stack spacing={1}>
              {shippingAddresses.length ? (
                shippingAddresses.map((address) => (
                  <MDButton
                    key={address.addressId}
                    variant={
                      String(selectedSavedAddress?.addressId) ===
                      String(address.addressId)
                        ? "contained"
                        : "outlined"
                    }
                    color="info"
                    onClick={() =>
                      setSelectedShippingAddressId(address.addressId)
                    }
                    sx={{
                      justifyContent: "flex-start",
                      textAlign: "left",
                      whiteSpace: "normal",
                    }}
                  >
                    {address.label}
                    {address.isPrimary ? " • Primary" : ""} —{" "}
                    {addressSummary(address)}
                  </MDButton>
                ))
              ) : (
                <Alert severity="warning">
                  No saved addresses yet. Use manual entry or add an address in
                  Profile.
                </Alert>
              )}
            </Stack>
          ) : (
            <Grid container spacing={2}>
              {[
                ["name", isMx ? "Nombre" : "Name", 12],
                ["street1", isMx ? "Calle y numero" : "Street", 12],
                [
                  "street2",
                  isMx
                    ? "Interior / Suite (opcional)"
                    : "Apt / Suite (optional)",
                  12,
                ],
                ["city", isMx ? "Ciudad" : "City", 6],
                ["state", isMx ? "Estado" : "State", 3],
                ["zip", isMx ? "Codigo postal" : "ZIP", 3],
                ["country", isMx ? "Pais" : "Country", 6],
                ["phone", isMx ? "Telefono" : "Phone", 6],
              ].map(([key, label, md]) => (
                <Grid item xs={12} md={md} key={key}>
                  <MDInput
                    label={label}
                    fullWidth
                    value={toAddress[key]}
                    onChange={(e) =>
                      setToAddress((a) => ({ ...a, [key]: e.target.value }))
                    }
                    disabled={!canCheckoutMx}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {isMx ? (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!wantsFacturaToRfc}
                    onChange={(e) => setWantsFacturaToRfc(e.target.checked)}
                  />
                }
                label="Need factura to my RFC?"
              />
              {wantsFacturaToRfc && satMissing.length ? (
                <Alert severity="warning">
                  SAT fields required: {satMissing.join(", ")}.
                  <Box sx={{ mt: 1 }}>
                    <MDButton
                      variant="outlined"
                      color="info"
                      onClick={() =>
                        navigate(
                          location.pathname.startsWith("/m/")
                            ? "/m/account/profile"
                            : "/account/profile",
                        )
                      }
                    >
                      Go to profile
                    </MDButton>
                  </Box>
                </Alert>
              ) : null}
            </Box>
          ) : null}
        </Stack>
      ) : null}

      {currentStep === "delivery" ? (
        <Stack spacing={2}>
          <Alert severity="info">Ship to: {activeShipToSummary}</Alert>
          <MDButton
            variant="gradient"
            color="info"
            onClick={onQuote}
            disabled={!canQuote || shippingLoading}
          >
            {shippingLoading ? "Quoting..." : "Get shipping rates"}
          </MDButton>
          {rates.length ? (
            <Stack spacing={1}>
              {rates.map((rate) => (
                <MDButton
                  key={String(rate.id)}
                  variant={
                    String(selectedRateId) === String(rate.id)
                      ? "contained"
                      : "outlined"
                  }
                  color="info"
                  onClick={() => setSelectedRateId(rate.id)}
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                    whiteSpace: "normal",
                  }}
                >
                  {formatRateLabel(rate, formatUSDForDisplay)}
                </MDButton>
              ))}
            </Stack>
          ) : null}
        </Stack>
      ) : null}

      {currentStep === "payment" ? (
        <Stack spacing={2}>
          {isDistributor && distributorHasCredit ? (
            <Alert severity="success">
              This distributor account can order on account with{" "}
              {checkoutProfile.paymentTerms || "configured credit terms"}.
            </Alert>
          ) : (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <MDButton
                variant={
                  paymentCollectionMethod === "stripe_card"
                    ? "gradient"
                    : "outlined"
                }
                color="info"
                onClick={() => setSelectedPaymentCollectionMethod("stripe_card")}
              >
                Credit card
              </MDButton>
              {!isMx && checkoutPaymentOptions.plaidTransferAvailable ? (
                <MDButton
                  variant={
                    paymentCollectionMethod === "plaid_transfer"
                      ? "gradient"
                      : "outlined"
                  }
                  color="info"
                  onClick={() =>
                    setSelectedPaymentCollectionMethod("plaid_transfer")
                  }
                >
                  Pay by bank account
                </MDButton>
              ) : null}
              {checkoutPaymentOptions.bankTransferAvailable ? (
                <MDButton
                  variant={
                    paymentCollectionMethod === "bank_transfer"
                      ? "gradient"
                      : "outlined"
                  }
                  color="info"
                  onClick={() =>
                    setSelectedPaymentCollectionMethod("bank_transfer")
                  }
                >
                  Bank transfer
                </MDButton>
              ) : null}
            </Box>
          )}
          {!stripePublishableKey &&
          paymentCollectionMethod === "stripe_card" ? (
            <Alert severity="info">
              Stripe publishable key is not configured for this frontend build.
            </Alert>
          ) : null}
          {paymentCollectionMethod === "plaid_transfer" ? (
            <Alert severity="info">
              Pay by bank account opens Plaid after review. The order stays
              unpaid until funds settle.
            </Alert>
          ) : null}
          {paymentCollectionMethod === "bank_transfer" ? (
            <Alert severity="info">
              Your order is placed immediately. Bank transfer instructions are
              emailed and shown after review. Fulfillment begins after staff
              confirms payment.
            </Alert>
          ) : null}
          {paymentCollectionMethod === "stripe_card" ? (
            <Alert severity="info">
              Card payment is collected securely through Stripe after review.
            </Alert>
          ) : null}
        </Stack>
      ) : null}

      {currentStep === "review" ? (
        <Stack spacing={1.5}>
          <Alert severity="info">Ship to: {activeShipToSummary}</Alert>
          {!usesDistributorRequestFlow && selectedRate ? (
            <Alert severity="info">
              Delivery: {formatRateLabel(selectedRate)}
            </Alert>
          ) : null}
          <MDTypography variant="body2" color="text" sx={{ opacity: 0.85 }}>
            Items subtotal:{" "}
            {formatUSDForDisplay((pricing?.itemsSubtotal ?? subtotal) || 0)}
            {" • "}Shipping:{" "}
            {formatUSDForDisplay((pricing?.shippingAmount ?? shippingAmount) || 0)}
            {Number(processingFeeAmount || pricing?.processingFeeAmount || 0) >
            0
              ? ` • ${
                  pricing?.processingFeeLabel ||
                  processingFeeConfig?.label ||
                  "Processing fee"
                }: ${formatUSDForDisplay(
                  (pricing?.processingFeeAmount ?? processingFeeAmount) || 0,
                )}`
              : ""}
            {" • "}Total: {formatUSDForDisplay(orderTotal || 0)}
          </MDTypography>
          {clientSecret && stripePromise ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <StripePayment
                clientSecret={clientSecret}
                orderNumber={orderNumber || "WEB"}
                onPaid={() => {
                  clear();
                  resetCheckoutSession();
                  setPostCheckoutMessage({
                    type: "success",
                    text: `Payment received for order ${
                      orderNumber || "created"
                    }.`,
                  });
                }}
              />
            </Elements>
          ) : null}
          {paymentCollectionMethod === "plaid_transfer" &&
          plaidSalesOrderDocumentId ? (
            <Alert severity="info">
              Plaid authorization is open. If the bank window was closed, use
              the review button to restart checkout for a fresh authorization.
            </Alert>
          ) : null}
        </Stack>
      ) : null}

      <Divider sx={{ my: 2 }} />
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <MDButton
          variant="outlined"
          color="info"
          onClick={
            currentStepIndex === 0
              ? () =>
                  navigate(
                    location.pathname.startsWith("/m/") ? "/m/cart" : "/cart",
                  )
              : goBack
          }
        >
          {currentStepIndex === 0 ? "Back to cart" : "Back"}
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          onClick={primaryAction}
          disabled={primaryDisabled}
        >
          {currentStep === "review"
            ? intentLoading
              ? "Creating..."
              : usesDistributorRequestFlow
                ? "Submit to distributor"
                : paymentCollectionMethod === "on_account"
                  ? "Place on account"
                : paymentCollectionMethod === "bank_transfer"
                  ? "Create transfer order"
                  : paymentCollectionMethod === "plaid_transfer"
                    ? plaidTransferLinkToken
                      ? "Plaid authorization ready"
                      : "Pay by bank account"
                  : clientSecret
                    ? "Payment ready"
                      : "Continue to payment"
            : `Continue to ${
                stepTitle[checkoutSteps[currentStepIndex + 1]] || "next"
              }`}
        </MDButton>
      </Box>
    </Card>
  );

  if (storefrontTheme.preset === "industrial") {
    return (
      <StorefrontLayout maxWidth={null}>
        <Box
          sx={{
            bgcolor:
              "radial-gradient(circle at 92% 4%, rgba(181,71,8,0.14), transparent 34%), #080B0E",
            minHeight: "100vh",
            py: 4,
          }}
        >
          <Container maxWidth="xl">
            <Grid container spacing={2}>
              <Grid item xs={12} lg={3}>
                <Box
                  sx={{
                    bgcolor: "#050608",
                    color: "#fff",
                    p: 2.25,
                    border: 0,
                    borderRadius: 0,
                    boxShadow: "0 24px 70px rgba(0,0,0,0.22)",
                    position: { lg: "sticky" },
                    top: 104,
                  }}
                >
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.62)",
                      fontWeight: 900,
                      fontFamily: "monospace",
                    }}
                  >
                    CHECKOUT CONTROL
                  </MDTypography>
                  <MDTypography
                    variant="h3"
                    sx={{ color: "#fff", fontWeight: 950, mt: 0.75, mb: 2 }}
                  >
                    Order release
                  </MDTypography>
                  <Stack spacing={1}>
                    {checkoutSteps.map((step, index) => (
                      <Box
                        key={step}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1,
                          py: 1,
                          borderTop: "1px solid rgba(255,255,255,0.12)",
                        }}
                      >
                        <MDTypography
                          variant="body2"
                          sx={{
                            color:
                              step === currentStep
                                ? "#fff"
                                : "rgba(255,255,255,0.58)",
                            fontWeight: 900,
                          }}
                        >
                          {stepTitle[step]}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          sx={{
                            color:
                              index <= currentStepIndex
                                ? "#fff"
                                : "rgba(255,255,255,0.42)",
                            fontFamily: "monospace",
                          }}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </MDTypography>
                      </Box>
                    ))}
                  </Stack>
                  <Divider
                    sx={{ my: 2, borderColor: "rgba(255,255,255,0.12)" }}
                  />
                  <MDTypography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.62)", fontWeight: 900 }}
                  >
                    Cart subtotal
                  </MDTypography>
                  <MDTypography
                    variant="h4"
                    sx={{ color: "#fff", fontWeight: 950 }}
                  >
                    {formatUSDForDisplay(subtotal || 0)}
                  </MDTypography>
                </Box>
              </Grid>
              <Grid item xs={12} lg={9}>
                {checkoutContent}
              </Grid>
            </Grid>
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  if (storefrontTheme.preset === "commerce") {
    return (
      <StorefrontLayout maxWidth={null}>
        <Box sx={{ bgcolor: "#F7F9FC", minHeight: "100vh", py: 4 }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                mb: 2,
                p: { xs: 2, md: 2.5 },
                bgcolor: "#fff",
                border: 0,
                borderRadius: 0,
                boxShadow: "0 24px 70px rgba(16,24,40,0.08)",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                gap={2}
              >
                <Box>
                  <MDTypography
                    variant="caption"
                    sx={{ color: "#2563eb", fontWeight: 900 }}
                  >
                    SECURE CHECKOUT
                  </MDTypography>
                  <MDTypography
                    variant="h3"
                    sx={{ color: "#0f172a", fontWeight: 950, lineHeight: 1 }}
                  >
                    Finish your order
                  </MDTypography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {checkoutSteps.map((step, index) => (
                    <Chip
                      key={step}
                      label={`${index + 1}. ${stepTitle[step]}`}
                      sx={{
                        borderRadius: 999,
                        bgcolor:
                          step === currentStep
                            ? "rgba(37,99,235,0.12)"
                            : "rgba(15,23,42,0.05)",
                        color: step === currentStep ? "#1d4ed8" : "#64748b",
                        fontWeight: 900,
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Box>
            {checkoutContent}
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  return <StorefrontLayout maxWidth="md">{checkoutContent}</StorefrontLayout>;
}
